import React, { useState, useEffect } from 'react';
import { Mail, Check, X, Phone, BellOff, Bell, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmailNotificationBanner = ({ userEmail }) => {
  const [dismissed, setDismissed] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Banner-dismiss uit localStorage (visueel verbergen)
    if (localStorage.getItem('emailNotificationBannerDismissed') === 'true') {
      setDismissed(true);
    }
    // Actuele voorkeur vanuit backend
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/dealer/settings/email-notifications`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => setEnabled(r.data.enabled !== false))
        .catch(() => {});
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem('emailNotificationBannerDismissed', 'true');
    setDismissed(true);
  };

  const toggleEnabled = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/dealer/settings/email-notifications`,
        { enabled: !enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEnabled(r.data.enabled);
    } catch (e) { /* stil */ }
    setSaving(false);
  };

  if (dismissed) return null;

  const isOn = enabled;
  return (
    <div
      className={`bg-gradient-to-r ${isOn ? 'from-green-50 to-blue-50 border-green-200' : 'from-zinc-50 to-zinc-100 border-zinc-300'} border rounded-lg p-4 mb-6 relative`}
      data-testid="email-notification-banner"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 p-1"
        aria-label="Sluiten"
        data-testid="email-banner-dismiss"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="space-y-3 pr-6">
        {/* Email notificatie */}
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-full ${isOn ? 'bg-green-100' : 'bg-zinc-200'}`}>
            <Mail className={`h-5 w-5 ${isOn ? 'text-green-600' : 'text-zinc-500'}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className={`font-semibold ${isOn ? 'text-green-800' : 'text-zinc-700'}`}>
                Email Meldingen {isOn ? 'Actief' : 'Uitgeschakeld'}
              </h3>
              {isOn ? <Check className="h-4 w-4 text-green-600" /> : <BellOff className="h-4 w-4 text-zinc-500" />}
            </div>
            <p className={`text-sm ${isOn ? 'text-green-700' : 'text-zinc-600'}`}>
              {isOn
                ? <>U ontvangt automatisch een email op <strong>{userEmail}</strong> bij nieuwe motoren.</>
                : <>U ontvangt momenteel <b>geen</b> emails bij nieuwe motoren.</>}
            </p>
            <button
              onClick={toggleEnabled}
              disabled={saving}
              className={`mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold transition ${
                isOn
                  ? 'bg-white border border-red-300 text-red-700 hover:bg-red-50'
                  : 'bg-green-600 text-white hover:bg-green-700'
              } disabled:opacity-50`}
              data-testid="email-notifications-toggle"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              {isOn ? <><BellOff className="w-3 h-3" /> Uitschakelen</> : <><Bell className="w-3 h-3" /> Inschakelen</>}
            </button>
          </div>
        </div>

        {/* SMS notificatie */}
        <div className="flex items-start gap-3 pt-2 border-t border-green-200">
          <div className="bg-blue-100 p-2 rounded-full">
            <Phone className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-blue-800">SMS Meldingen</h3>
              <Check className="h-4 w-4 text-blue-600" />
            </div>
            <p className="text-sm text-blue-700">
              U kunt ook SMS meldingen ontvangen over nieuwe motoren.
            </p>
            <div className="mt-2 bg-white rounded-lg p-3 border border-blue-200">
              <p className="text-sm font-medium text-gray-800">📱 Sla dit nummer op in uw contacten:</p>
              <p className="text-lg font-bold text-blue-600 mt-1">+31 6 24264861</p>
              <p className="text-xs text-gray-500 mt-1">Opslaan als &quot;Moto Import&quot;</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailNotificationBanner;
